import { useMany, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Flex, Progress, Table, Tag, Tooltip, Typography } from 'antd';
import type {
  CycleListOut,
  ProfileOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { CycleCloseReason, CycleKind } from '@/api/generated/schemas';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ProfileSelectFilter } from '@/components/account/ProfileSelectFilter';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { ProductSelectFilter } from '@/components/user_catalog/publications/ProductSelectFilter';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { orDash } from '@/components/common/show/shared';
import { BooleanFilter } from '@/components/common/BooleanFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { CycleKindTag, CycleStatusTag } from '@/components/settlement/tags';
import { formatDate, formatFraction, formatSecondsClock } from '@/utils/format';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const CycleList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const { tableProps, sorters, setFilters, filters } =
    useAdminTable<CycleListOut>({
      pagination: { pageSize: 20 },
      resource: 'settlement.cycles',
      sorters: {
        // covered_* are generated and unindexed server-side; the PK stays cheap.
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs one extra request instead of one per row.
  const profileIds = [
    ...new Set(rows.map((r) => r.profile_id).filter(Boolean)),
  ];

  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });

  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  const publisherIds = [
    ...new Set(rows.map((r) => r.publisher_id).filter(Boolean)),
  ];

  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });

  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const productIds = [
    ...new Set(rows.map((r) => r.product_id).filter(Boolean)),
  ];

  const publicationByProduct = usePublicationsByProducts(productIds);

  return (
    <List>
      <Table
        {...rowNav('settlement.cycles', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="publisher_id"
          title={t('settlement.cycles.fields.publisher_id')}
          render={(value: number) =>
            orDash(
              value != null && (
                <PublisherIconLink
                  publisherId={value}
                  publisher={publisherMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              only="imprints"
              placeholder={t('settlement.cycles.filter.publisher_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="profile_id"
          title={t('settlement.cycles.fields.profile_id')}
          render={(value: number) =>
            orDash(
              value != null && (
                <ProfileIconLink
                  profileId={value}
                  profile={profileMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProfileSelectFilter
              field="profile_id"
              placeholder={t('settlement.cycles.filter.profile_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_id"
          title={t('settlement.cycles.fields.product_id')}
          render={(value: number, record: CycleListOut) => {
            const publication = publicationByProduct.get(value);
            return orDash(
              value != null && (
                <Flex gap={4} align="center" wrap>
                  {publication ? (
                    <PublicationInfoPopover
                      publication={publication}
                      publicationId={publication.id}
                    />
                  ) : (
                    value
                  )}
                  <Typography.Text type="secondary">
                    {`v${record.product_version}`}
                  </Typography.Text>
                </Flex>
              )
            );
          }}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProductSelectFilter
              field="product_id"
              placeholder={t('settlement.cycles.filter.product_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          key="product_format"
          title={t('settlement.cycles.fields.product_format')}
          width={130}
          render={(_, record: CycleListOut) => {
            const kind = productKind(
              publicationByProduct.get(record.product_id),
              record.product_id
            );
            return orDash(kind && <ProductFormatTag kind={kind} />);
          }}
        />

        <Table.Column
          dataIndex="pass_number"
          title={t('settlement.cycles.fields.pass_number')}
          sorter
          sortOrder={getSortOrder(sorters, 'pass_number')}
          render={(value: number) => orDash(value)}
          filtered={isFieldFiltered(filters as CrudFilter[], 'pass_number')}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="pass_number"
              min={1}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="cycle_kind"
          title={t('settlement.cycles.fields.cycle_kind')}
          render={(value: CycleKind) =>
            orDash(value && <CycleKindTag value={value} />)
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'cycle_kind')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="cycle_kind"
              minWidth={160}
              placeholder={t('settlement.cycles.filter.cycle_kind')}
              options={Object.values(CycleKind).map((kind) => ({
                label: t(`settlement.cycles.kind.${kind}`),
                value: kind,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="started_at"
          title={t('settlement.cycles.fields.started_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'started_at')}
          render={(value: string) => orDash(formatDate(value))}
          filtered={isFieldFiltered(filters as CrudFilter[], 'started_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="started_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="covered_fraction"
          title={t('settlement.cycles.fields.coverage')}
          sorter
          sortOrder={getSortOrder(sorters, 'covered_fraction')}
          render={(value: string, record: CycleListOut) => (
            <Flex vertical gap={2} style={{ minWidth: 150 }}>
              <Tooltip title={value}>
                <Progress
                  percent={Math.round(Number(value) * 100)}
                  format={() => formatFraction(value)}
                  size="small"
                  style={{ marginBottom: 0 }}
                />
              </Tooltip>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatSecondsClock(record.covered_seconds, {
                  alwaysHours: true,
                })}
              </Typography.Text>
            </Flex>
          )}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'covered_fraction'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="covered_fraction"
              min={0}
              max={1}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="is_billable"
          title={t('settlement.cycles.fields.is_billable')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'default'}>
              {t(value ? 'common.yes' : 'common.no')}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_billable')}
          filterDropdown={({ confirm, clearFilters }) => (
            <BooleanFilter
              field="is_billable"
              placeholder={t('settlement.cycles.filter.is_billable')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="close_reason"
          title={t('settlement.cycles.fields.status')}
          render={(value: CycleCloseReason | null, record: CycleListOut) => (
            <CycleStatusTag status={record.status} closeReason={value} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'close_reason')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="close_reason"
              minWidth={180}
              placeholder={t('settlement.cycles.filter.close_reason')}
              options={Object.values(CycleCloseReason).map((reason) => ({
                label: t(`settlement.cycles.close_reason.${reason}`),
                value: reason,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />
      </Table>
    </List>
  );
};
